// Tipos de cupom
export const COUPON_TYPES = {
  PERCENTAGE: 'percentage',
  FIXED_AMOUNT: 'fixed_amount',
  QUANTITY: 'quantity'
}

export class Coupon {
  constructor() {
    this.id = 0
    this.code = ''
    this.description = ''
    this.type = COUPON_TYPES.PERCENTAGE
    this.value = 0
    this.minimumAmount = 0
    this.maximumDiscount = 0
    this.usageLimit = 0
    this.userLimit = 1
    this.usedCount = 0
    this.validFrom = null
    this.validUntil = null
    this.isActive = true
    this.createdBy = 0
    this.createdAt = null
    this.updatedAt = null
    this.usages = []
  }

  static FromObj(obj) {
    const instance = new Coupon()
    
    instance.id = obj.id || 0
    instance.code = obj.code || obj.name || ''
    instance.description = obj.description || ''
    
    // Mapear tipo do cupom
    if (obj.couponType === 1 || obj.coupon_type === 1) {
      instance.type = COUPON_TYPES.PERCENTAGE
    } else if (obj.couponType === 2 || obj.coupon_type === 2) {
      instance.type = COUPON_TYPES.FIXED_AMOUNT
    } else if (obj.couponType === 3 || obj.coupon_type === 3) {
      instance.type = COUPON_TYPES.QUANTITY
    } else {
      instance.type = obj.type || COUPON_TYPES.PERCENTAGE
    }
    
    instance.value = parseFloat(obj.value) || 0
    instance.minimumAmount = parseFloat(obj.minimumAmount || obj.minimum_amount || 0)
    instance.maximumDiscount = parseFloat(obj.maximumDiscount || obj.maximum_discount || 0)
    instance.usageLimit = parseInt(obj.usageLimit || obj.usage_limit || obj.maxUsage || obj.max_usage || 0)
    instance.userLimit = parseInt(obj.userLimit || obj.user_limit || 1)
    instance.usedCount = parseInt(obj.usedCount || obj.used_count || obj.used || 0)
    instance.validFrom = obj.validFrom || obj.valid_from || null
    instance.validUntil = obj.validUntil || obj.valid_until || obj.expireAt || obj.expire_at || null
    instance.isActive = obj.isActive !== undefined ? obj.isActive : (obj.active !== undefined ? obj.active : true)
    instance.createdBy = obj.createdBy || obj.created_by || 0
    instance.createdAt = obj.createdAt || obj.created_at || null
    instance.updatedAt = obj.updatedAt || obj.updated_at || null
    instance.usages = obj.usages || []
    
    return instance
  }

  // Validações
  isValid() {
    const now = new Date()
    
    // Verificar se está ativo
    if (!this.isActive) return false
    
    // Verificar data de validade
    if (this.validFrom && now < new Date(this.validFrom)) return false
    if (this.validUntil && now > new Date(this.validUntil)) return false
    
    // Verificar limite de uso
    if (this.usageLimit > 0 && this.usedCount >= this.usageLimit) return false
    
    return true
  }

  canBeUsedBy(userId) {
    if (!this.isValid()) return false
    
    // Verificar limite por usuário
    if (this.userLimit > 0) {
      const userUsages = this.usages.filter(usage => usage.userId === userId)
      if (userUsages.length >= this.userLimit) return false
    }
    
    return true
  }

  // Cálculos
  calculateDiscount(amount) {
    if (!this.isValid() || amount < this.minimumAmount) {
      return 0
    }
    
    let discount = 0
    
    if (this.type === COUPON_TYPES.PERCENTAGE) {
      discount = (amount * this.value) / 100
      
      // Aplicar desconto máximo se definido
      if (this.maximumDiscount > 0) {
        discount = Math.min(discount, this.maximumDiscount)
      }
    } else if (this.type === COUPON_TYPES.FIXED_AMOUNT) {
      discount = this.value
    }
    
    // Desconto não pode ser maior que o valor total
    return Math.min(discount, amount)
  }

  // Informações
  getTypeLabel() {
    switch (this.type) {
      case COUPON_TYPES.PERCENTAGE:
        return 'Percentual'
      case COUPON_TYPES.FIXED_AMOUNT:
        return 'Valor Fixo'
      case COUPON_TYPES.QUANTITY:
        return 'Unitário'
      default:
        return 'Percentual'
    }
  }

  getValueLabel() {
    switch (this.type) {
      case COUPON_TYPES.PERCENTAGE:
        return `${this.value}%`
      case COUPON_TYPES.FIXED_AMOUNT:
        return `R$ ${this.value.toFixed(2)}`
      case COUPON_TYPES.QUANTITY:
        return `${this.value} unidades`
      default:
        return `${this.value}%`
    }
  }

  getUsagePercentage() {
    if (this.usageLimit === 0) return 0
    return (this.usedCount / this.usageLimit) * 100
  }

  getRemainingUsages() {
    if (this.usageLimit === 0) return 'Ilimitado'
    return this.usageLimit - this.usedCount
  }

  isExpired() {
    if (!this.validUntil) return false
    return new Date() > new Date(this.validUntil)
  }

  isExpiringSoon(days = 7) {
    if (!this.validUntil) return false
    const expiryDate = new Date(this.validUntil)
    const warningDate = new Date()
    warningDate.setDate(warningDate.getDate() + days)
    return expiryDate <= warningDate
  }

  isNotYetValid() {
    if (!this.validFrom) return false
    return new Date() < new Date(this.validFrom)
  }

  isUsageLimitReached() {
    return this.usageLimit > 0 && this.usedCount >= this.usageLimit
  }

  // Dados para formulário
  toFormData() {
    return {
      id: this.id,
      code: this.code,
      description: this.description,
      type: this.type,
      value: this.value,
      minimum_amount: this.minimumAmount,
      maximum_discount: this.maximumDiscount,
      usage_limit: this.usageLimit,
      user_limit: this.userLimit,
      valid_from: this.validFrom,
      valid_until: this.validUntil,
      is_active: this.isActive
    }
  }
}

export class CouponUsage {
  constructor() {
    this.id = 0
    this.userId = 0
    this.caseId = 0
    this.originalAmount = 0
    this.discountAmount = 0
    this.finalAmount = 0
    this.createdAt = null
  }

  static FromObj(obj) {
    const instance = new CouponUsage()
    
    instance.id = obj.id || 0
    instance.userId = obj.userId || obj.user_id || 0
    instance.caseId = obj.caseId || obj.case_id || 0
    instance.originalAmount = obj.originalAmount || obj.original_amount || 0
    instance.discountAmount = obj.discountAmount || obj.discount_amount || 0
    instance.finalAmount = obj.finalAmount || obj.final_amount || 0
    instance.createdAt = obj.createdAt || obj.created_at || null
    
    return instance
  }

  getDiscountPercentage() {
    if (this.originalAmount === 0) return 0
    return (this.discountAmount / this.originalAmount) * 100
  }

  getSavings() {
    return this.discountAmount
  }
}

export class CouponValidation {
  constructor() {
    this.coupon = null
    this.discountAmount = 0
    this.finalAmount = 0
    this.isValid = false
    this.errorMessage = ''
  }

  static FromObj(obj) {
    const instance = new CouponValidation()
    
    instance.coupon = obj.coupon ? Coupon.FromObj(obj.coupon) : null
    instance.discountAmount = obj.discountAmount || obj.discount_amount || 0
    instance.finalAmount = obj.finalAmount || obj.final_amount || 0
    instance.isValid = obj.isValid !== undefined ? obj.isValid : false
    instance.errorMessage = obj.errorMessage || obj.error_message || ''
    
    return instance
  }

  hasError() {
    return !this.isValid && this.errorMessage.length > 0
  }

  getErrorMessage() {
    return this.errorMessage
  }
}